import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { ProjectRollingSummaryView } from "./ProjectRollingSummaryView";
import { ParticipantList } from "./ParticipantList";
import { ParticipantSummaryDetail } from "./ParticipantSummaryDetail";

type ViewMode = "project" | "characters";

interface SummaryFullViewProps {
  projectId: string;
  /** Accent color (CSS var or hex) used for active pills. Defaults to #EC4899. */
  accentColor?: string;
}

/**
 * Reusable full-page summary view with BY HOUR / BY CHARACTER toggle.
 * Mirrors the ExecutiveLayout content area so all roles get the same experience.
 */
export const SummaryFullView: React.FC<SummaryFullViewProps> = ({
  projectId,
  accentColor = "#EC4899",
}) => {
  const { t } = useTranslation();
  const [viewMode, setViewMode] = useState<ViewMode>("project");
  // ParticipantList.onSelect entrega el objeto Participant; ParticipantSummaryDetail
  // acepta tanto el id string como el objeto, así que guardamos cualquiera de los dos.
  const [selectedParticipantId, setSelectedParticipantId] = useState<
    string | { id?: string; [key: string]: any } | null
  >(null);

  const modeButtons: { value: ViewMode; labelKey: string; icon: string }[] = [
    { value: "project", labelKey: "executive.modeProject", icon: "bx-folder" },
    {
      value: "characters",
      labelKey: "executive.modeCharacters",
      icon: "bx-group",
    },
  ];

  return (
    <div style={{ height: "100%", display: "flex", flexDirection: "column" }}>
      {/* Sub-header: mode toggle */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          padding: "14px 0",
          flexShrink: 0,
          flexWrap: "wrap",
        }}
      >
        {modeButtons.map((btn) => {
          const active = viewMode === btn.value;
          return (
            <button
              key={btn.value}
              onClick={() => setViewMode(btn.value)}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                background: active
                  ? `linear-gradient(135deg, ${accentColor}, ${accentColor}dd)`
                  : "rgba(255,255,255,0.04)",
                color: active ? "#fff" : "var(--color-text-muted)",
                border: `1px solid ${active ? "transparent" : "rgba(255,255,255,0.1)"}`,
                padding: "8px 20px",
                borderRadius: "var(--border-radius-pill, 9999px)",
                cursor: "pointer",
                fontSize: "0.75rem",
                fontWeight: 700,
                letterSpacing: "1px",
                textTransform: "uppercase",
                transition: "all 0.25s ease",
                fontFamily: "inherit",
                boxShadow: active ? `0 2px 12px ${accentColor}40` : "none",
              }}
              onMouseEnter={(e) => {
                if (!active) {
                  e.currentTarget.style.background = "rgba(255,255,255,0.08)";
                  e.currentTarget.style.borderColor = "rgba(255,255,255,0.2)";
                }
              }}
              onMouseLeave={(e) => {
                if (!active) {
                  e.currentTarget.style.background = "rgba(255,255,255,0.04)";
                  e.currentTarget.style.borderColor = "rgba(255,255,255,0.1)";
                }
              }}
            >
              <i className={`bx ${btn.icon}`} style={{ fontSize: 16 }} />
              {t(btn.labelKey)}
            </button>
          );
        })}
      </div>

      {/* Content area */}
      <div style={{ flex: 1, overflowY: "scroll" }}>
        {selectedParticipantId ? (
          <ParticipantSummaryDetail
            participantId={selectedParticipantId}
            projectId={projectId}
            onBack={() => setSelectedParticipantId(null)}
          />
        ) : viewMode === "project" ? (
          <ProjectRollingSummaryView projectId={projectId} />
        ) : (
          <ParticipantList
            projectId={projectId}
            onSelect={(id) => setSelectedParticipantId(id)}
            summaryType="daily"
          />
        )}
      </div>
    </div>
  );
};

export default SummaryFullView;
